import React, { useState, useEffect, useContext, useCallback } from 'react';
import Layout from '../../components/layouts/Main';
import { AuthContext } from '../../context/AuthContext';
import { FaSave, FaTimes, FaWarehouse, FaBox, FaArrowRight, FaPlus } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import config from '../../config';
import axios from 'axios';

const StockTransferInstanceCreate = () => {
  const { loggedUser: user } = useContext(AuthContext);
  const username = user?.username;
  const domainName = username;
  const apiUrl = config.API_BASE_URL;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    product: '',
    sourceWarehouse: '',
    sourceProductStock: '',
    quantity: '',
    unit: '',
    batchNumber: '',
    remarks: ''
  });

  const [products, setProducts] = useState([]);
  const [stockLocations, setStockLocations] = useState([]);
  const [selectedStockEntry, setSelectedStockEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [fetchingStock, setFetchingStock] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/products`, {
        headers: { 'X-Subdomain': domainName },
        withCredentials: true
      });

      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally{
      setLoading(false);
    }
  }, [apiUrl, domainName]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const fetchProductStockLocations = useCallback(async () => {
    if (!formData.product) return;

    try {
      setFetchingStock(true);
      const response = await axios.get(
        `${apiUrl}/stock-transfer-instances/products/${formData.product}/stock-locations`,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Subdomain': domainName
          },
          withCredentials: true
        }
      );

      const data = response.data;
      if (data.success) {
        setStockLocations(data.stockLocations || []);
      } else {
        toast.error(data.message || 'Failed to fetch stock locations');
        setStockLocations([]);
      }
    } catch (error) {
      console.error('Error fetching stock locations:', error);
      toast.error('Failed to load stock locations');
      setStockLocations([]);
    } finally {
      setFetchingStock(false);
    }
  }, [apiUrl, domainName, formData.product]);

  useEffect(() => {
    fetchProductStockLocations();
  }, [formData.product, fetchProductStockLocations]);

  const handleProductChange = (productId) => {
    setFormData(prev => ({
      ...prev,
      product: productId,
      sourceWarehouse: '',
      sourceProductStock: '',
      quantity: ''
    }));
    setSelectedStockEntry(null);
    setStockLocations([]);
  };

  const handleWarehouseChange = (warehouseId) => {
    const location = stockLocations.find(loc => loc.warehouse._id === warehouseId);
    if (location) {
      setFormData(prev => ({
        ...prev,
        sourceWarehouse: warehouseId,
        sourceProductStock: location._id || warehouseId,
        quantity: '',
        unit: location.product.unit,
        batchNumber: location.batchNumber || ''
      }));
      setSelectedStockEntry(location);
    }
  };



  const validateForm = () => {
    if (!formData.product) return 'Product is required';
    if (!formData.sourceWarehouse) return 'Source warehouse is required';
    if (!formData.quantity || formData.quantity <= 0) return 'Valid quantity is required';
    if (selectedStockEntry && parseFloat(formData.quantity) > selectedStockEntry.availableStock) {
      return 'Quantity exceeds available stock';
    }
    return null;
  };

  const getSelectedProduct = () => {
    return products.find(p => p._id === formData.product);
  };

  const getSelectedLocation = () => {
    return stockLocations.find(loc => loc.warehouse._id === formData.sourceWarehouse);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    try {
      setSubmissionLoading(true);

      const instanceData = {
        ...formData,
        quantity: parseFloat(formData.quantity)
      };

      const response = await axios.post(`${apiUrl}/stock-transfer-instances`, instanceData, {
        headers: {
          'Content-Type': 'application/json',
          'X-Subdomain': domainName
        },
        withCredentials: true
      });

      const data = response.data;

      if (data.success) {
        toast.success('Stock transfer instance created successfully!');
        navigate('/stock-transfer-instances');
      } else {
        toast.error(data.message || 'Failed to create stock transfer instance');
      }
    } catch (error) {
      console.error('Error creating instance:', error);
      toast.error('Failed to create stock transfer instance');
    } finally {
      setSubmissionLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  const selectedProduct = getSelectedProduct();
  const selectedLocation = getSelectedLocation();

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          <li className="breadcrumb-item"><Link to="/stock">Stock</Link></li>
          <li className="breadcrumb-item"><Link to="/stock-transfer-instances">Stock Transfers</Link></li>
          <li className="breadcrumb-item active">Create New Instance</li>
        </ol>
      </nav>
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="fw-bold text-primary">
                <FaPlus className="me-2" />
                New Stock Transfer Instance
              </h4>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate('/stock-transfer-instances')}
              >
                <FaTimes className="me-1" />
                Cancel
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Product Selection */}
            <div className="col-md-6">
              <div className="card">
                <div className="card-header bg-light">
                  <h5 className="card-title mb-0">
                    <FaBox className="me-2 text-primary" />
                    Product Selection
                  </h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label">Product *</label>
                    <select
                      className="form-select"
                      value={formData.product}
                      onChange={(e) => handleProductChange(e.target.value)}
                    >
                      <option value="">Select product</option>
                      {products.filter(p => p.isActive).map(product => (
                        <option key={product._id} value={product._id}>
                          {product.productName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.product && (
                    <>
                      {fetchingStock ? (
                        <div className="text-center py-3">
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading stock...</span>
                          </div>
                          <small className="text-muted d-block mt-1">Loading stock locations...</small>
                        </div>
                      ) : stockLocations.length === 0 ? (
                        <div className="alert alert-info">
                          No stock locations found for this product.
                        </div>
                      ) : (
                        <>
                          {/* Warehouse Selection */}
                          <div className="mb-3">
                            <label className="form-label">Warehouse *</label>
                            <select
                              className="form-select"
                              value={formData.sourceWarehouse}
                              onChange={(e) => handleWarehouseChange(e.target.value)}
                            >
                              <option value="">Select warehouse</option>
                              {stockLocations.map(location => (
                                <option key={location.warehouse._id} value={location.warehouse._id}>
                                  {location.warehouse.label} - {location.isPacked ? 'Packed' : 'Unpacked'} ({location.availableStock} {location.product.unit})
                                  {location.packingMaterial ? ` - ${location.packingMaterial.materialName}` : ''}
                                </option>
                              ))}
                            </select>
                          </div>

                          {selectedLocation && (
                            <>
                              {/* Quantity */}
                              <div className="mb-3">
                                <label className="form-label">Quantity *</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={formData.quantity}
                                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                                  placeholder={`Max: ${selectedLocation.availableStock}`}
                                  min="0"
                                  max={selectedLocation.availableStock}
                                  step="0.01"
                                  required
                                />
                                <small className="form-text text-muted">
                                  Available: {selectedLocation.availableStock} {selectedLocation.product.unit}
                                </small>
                                {formData.quantity && parseFloat(formData.quantity) > selectedLocation.availableStock && (
                                  <small className="text-danger d-block">
                                    Quantity exceeds available stock
                                  </small>
                                )}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Instance Details */}
            <div className="col-md-6">
              <div className="card">
                <div className="card-header bg-light">
                  <h5 className="card-title mb-0">
                    <FaWarehouse className="me-2 text-success" />
                    Additional Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row d-none">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Unit</label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={formData.unit}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Batch Number</label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={formData.batchNumber}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Remarks</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.remarks}
                      onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                      placeholder="Optional remarks for this instance"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="card mt-4">
            <div className="card-body">
              <div className="d-flex justify-content-end gap-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate('/stock-transfer-instances')}
                >
                  <FaTimes className="me-1" />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submissionLoading || !formData.sourceWarehouse || !formData.quantity}
                >
                  {submissionLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Creating Instance...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-1" />
                      Create Instance
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Preview */}
        {formData.product && formData.sourceWarehouse && formData.quantity && (
          <div className="card mt-4">
            <div className="card-header bg-info text-white">
              <h6 className="mb-0">
                <FaArrowRight className="me-2" />
                Instance Preview
              </h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <strong>Product:</strong> {selectedProduct?.productName}
                </div>
                <div className="col-md-6">
                  <strong>Warehouse:</strong> {selectedLocation?.warehouse.label}
                </div>
                <div className="col-md-6">
                  <strong>Quantity:</strong> {formData.quantity} {formData.unit}
                </div>
                <div className="col-md-6">
                  <strong>Batch:</strong> {selectedStockEntry?.batchNumber || 'N/A'}
                </div>
              </div>
              {selectedStockEntry?.packingMaterial && (
                <div className="mt-2">
                  <strong>Packing Material:</strong> {selectedStockEntry.packingMaterial.materialName}
                </div>
              )}
              {formData.remarks && (
                <div className="mt-2">
                  <strong>Remarks:</strong> {formData.remarks}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StockTransferInstanceCreate;
